import clsx from 'clsx';
import { useState } from 'react';

interface ITextArea {
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register?: any;
  error?: string | string[];
  name: string;
  label?: string;
  placeholder?: string;
  value?: string;
  className?: string;
  textAreaClassName?: string;
  readOnly?: boolean;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
  autoFocus?: boolean;
  isRequired?: boolean;
  maxLength?: number;
  rows?: number;
  showCount?: boolean;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
}

const TextArea = ({
  disabled,
  error,
  register,
  name,
  placeholder,
  value,
  label,
  className,
  textAreaClassName,
  readOnly,
  autoFocus = false,
  onBlur,
  isRequired,
  maxLength,
  rows = 4,
  showCount = false,
  onChange,
}: ITextArea) => {
  const registered = register?.(name);
  const [currentLength, setCurrentLength] = useState(value?.length || 0);

  return (
    <div className={clsx('w-full flex flex-col gap-1.5', className)}>
      {label && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-neutral-primary leading-5"
        >
          <span>
            {label} {isRequired && <span className="text-red-500">*</span>}
          </span>
        </label>
      )}
      <div
        className={clsx(
          'group w-full rounded-lg flex flex-col shadow-s',
          disabled
            ? 'border border-neutral-light-200 bg-neutral-100 focus-within:outline-none'
            : error
              ? 'border border-error-default focus-within:outline-none bg-white'
              : 'border border-neutral-light-200 focus-within:outline-2 bg-white focus-within:outline-offset-2 hover:border-brand-default focus-within:border-[#e3ecff] focus-within:outline-[#e3ecff] text-neutral-900 font-semibold text-base',
          {
            'border-neutral-light-200! focus-within:outline-none!': readOnly,
          }
        )}
      >
        <textarea
          disabled={disabled}
          readOnly={readOnly}
          placeholder={placeholder}
          value={register?.value || value}
          maxLength={maxLength}
          rows={rows}
          {...registered}
          onChange={(e) => {
            setCurrentLength(e.target.value.length);
            registered?.onChange?.(e);
            onChange?.(e);
          }}
          onBlur={(e) => {
            if (e.target.value) {
              e.target.value = e.target.value.trim().replace(/\s+/g, ' ');
            }
            registered?.onBlur?.(e);
            onBlur?.(e);
          }}
          autoFocus={autoFocus}
          className={clsx(
            'w-full focus:outline-none border-none py-2.5 px-3 rounded-lg text-sm font-medium text-neutral-900 resize-none',
            'placeholder:font-medium placeholder:text-neutral-disabled autofill:bg-transparent!',
            disabled &&
              'text-neutral-disabled cursor-not-allowed bg-neutral-50 focus:outline-none',
            textAreaClassName
          )}
        />
        {showCount && maxLength && (
          <div className="flex justify-end px-3 pb-2">
            <span className="text-[10px] text-neutral-disabled">
              {currentLength}/{maxLength}
            </span>
          </div>
        )}
      </div>
      {error &&
        (typeof error === 'object' ? (
          error.map((e, i) => (
            <p key={i} className="text-sm font-normal text-error-default">
              {e}
            </p>
          ))
        ) : (
          <p className="text-sm font-normal text-error-default">{error}</p>
        ))}
    </div>
  );
};

export default TextArea;
